import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Package, Pencil, Check, X, List, BarChart2,
  Sparkles, MessageSquare, Building2, Hash, Baby, Calendar, User, ZoomIn,
  ClipboardList, TriangleAlert, Tag,
} from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import RejectModal from "../components/RejectModal";
import ConfirmModal from "../components/ConfirmModal";
import ApproveModal from "../components/ApproveModal";
import { useAuth } from "../context/AuthContext";
import { can, fmtDate, AGE_GROUPS_DISPLAY } from "../data/constants";
import { useReviewDetail, parseReviewFiles, parseOcrIngredients, parseOcrNutrition } from "../hooks/useReviewDetail";
import { submitReviewDecision } from "../services/reviewsService";
import StageScoreCard, { SCORE_STAGE_RANGES, scoreTone, parseStageScores } from "../components/ProductScoreStages";

const AGE_GROUPS_LIST = [
  /* { key: "0_6_months", label: "0–6 Months" }, */  // commented: hide 0-24 months
  /* { key: "6_12_months", label: "6–12 Months" }, */ // commented: hide 0-24 months
  { key: "1_2_years", label: "1–2 Years" },
  { key: "2_3_years", label: "2–3 Years" },
  { key: "3_5_years", label: "3–5 Years" },
  { key: "5_7_years", label: "5–7 Years" },
  { key: "7_10_years", label: "7–10 Years" },
];

/** Small labelled meter bar. */
function Meter({ value, color }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="pw-meter">
      <i style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/** Metadata chip in the page header. */
function Chip({ icon: Icon, label, value, mono, highlight }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span className={`pw-chip${mono ? " pw-chip-mono" : ""}${highlight ? " pw-chip-hi" : ""}`} title={`${label}: ${value}`}>
      <Icon size={12} />
      <span style={{ color: "#37BEB0" }}>{label}</span>
      <b>{value}</b>
    </span>
  );
}

/** Skeleton placeholder shown while the review detail request is in flight. */
function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 lg:gap-5 items-start">
      <div className="flex flex-col gap-4">
        <div className="pw-card"><div className="pw-sec-b">
          <div className="pw-skel" style={{ height: 230, borderRadius: 13 }} />
          <div className="pw-thumbs">
            {[0, 1, 2, 3].map((i) => <div key={i} className="pw-skel" style={{ width: 52, height: 52, borderRadius: 10 }} />)}
          </div>
        </div></div>
        <div className="pw-card"><div className="pw-sec-b">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "9px 0" }}>
              <div className="pw-skel" style={{ height: 10, width: 78 }} />
              <div className="pw-skel" style={{ height: 10, width: 110 }} />
            </div>
          ))}
        </div></div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="pw-card"><div className="pw-sec-b">
          <div className="pw-ages">
            {AGE_GROUPS_LIST.map((g) => <div key={g.key} className="pw-skel" style={{ height: 74, borderRadius: 12 }} />)}
          </div>
        </div></div>
        <div className="pw-card"><div className="pw-sec-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="pw-skel" style={{ height: 12, width: `${90 - i * 8}%` }} />)}
        </div></div>
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { user } = useAuth();
  const { id: productId } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const listSearch = location.state?.listSearch;
  const goToList  = () => navigate(listSearch ? `/products?${listSearch}` : "/products");
  const { products, setProducts, addToast } = useOutletContext();

  const [rejectM,  setRejectM]  = useState(false);
  const [approveM, setApproveM] = useState(false);
  const [confirmM, setConfirmM] = useState({ open: false });
  const [imgI,     setImgI]     = useState(0);
  const [imgLoading, setImgLoading] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [concernModal, setConcernModal] = useState({ open: false, name: "", concern: "" });

  // ── Fetch review details for this product ─────────────────────────────
  // Calls GET /api/v1/admin/products/{product_id}/review-details
  const existingProduct = products.find((x) => x.id === productId);
  const { detail, loading: loadingDetail, error: detailError } = useReviewDetail(
    productId,
    existingProduct?.ageGroup || null
  );

  // ── Find or construct product object ───────────────────────────────────
  const p = useMemo(() => {
    const existing = existingProduct;
    const backendAgeGroup = detail?.age_group || detail?.submission?.age_group || null;

    if (existing) {
      return {
        ...existing,
        ageGroup: existing.ageGroup || backendAgeGroup,
      };
    }

    if (!detail) return null;

    // Dynamically construct product shape from detail when not found in list (e.g. on direct page reload)
    const sub = detail.submission || {};
    const gtin = sub.gtin || "N/A";
    const fileCount = Array.isArray(sub.files) ? sub.files.length : 0;

    return {
      id: productId,
      product_id: detail.product_id || productId,
      name: sub.product_name || "Unknown Product",
      brand: sub.brand_name || "Unknown Brand",
      category: sub.category || detail.category || "",
      gtin: gtin || detail.gtin || "N/A",
      status: detail.decision || detail.status || "pending",
      emoji: "🔍",
      bg: "rgba(55,190,176,.12)",
      createdDate: sub.submitted_at || detail.created_at,
      createdBy: sub.agent?.name || sub.agent_id || "Agent",
      description: `GTIN: ${gtin} · ${fileCount} file${fileCount !== 1 ? "s" : ""} submitted`,
      ingredients: "",
      nutrients: "",
      files: [],
      approvedBy: null,
      approvedDate: null,
      rejectReason: detail.review_notes || null,
      isReview: true,
      ageGroup: backendAgeGroup,
    };
  }, [products, productId, detail]);

  // ── Derived display values from review detail ──────────────────────────
  const reviewFiles = useMemo(() => parseReviewFiles(detail), [detail]);

  const ocrIngredients = useMemo(
    () => parseOcrIngredients(detail?.ingredients || detail?.ocr?.ingredients),
    [detail]
  );
  const ocrNutrition = useMemo(
    () => parseOcrNutrition(detail?.nutrition_facts || detail?.ocr?.nutrition_facts),
    [detail]
  );

  // ── Image carousel items ───────────────────────────────────────────────
  const imgs = useMemo(() => {
    if (!p) return [];

    // Prefer real review files with S3 URLs if they exist
    if (reviewFiles.length > 0) return reviewFiles;

    if (detail?.image_url) {
      return [{
        fileId: "image_url",
        fileType: "front_label",
        fileName: "Front Image",
        url: detail.image_url,
        emoji: "📦",
        bg: "rgba(55,190,176,.12)",
      }];
    }

    // Fallback placeholder set for standard products without S3 files
    return [
      { emoji: p.emoji, bg: p.bg },
      { emoji: "📋",    bg: "rgba(55,190,176,.12)" },
      { emoji: "🏷️",    bg: "rgba(12,97,112,.1)" },
    ];
  }, [p, reviewFiles, detail]);

  // Set loading to true when carousel index or image URL changes
  useEffect(() => {
    if (imgs[imgI]?.url) {
      setImgLoading(true);
    } else {
      setImgLoading(false);
    }
  }, [imgI, imgs]);

  // Keyboard navigation for the fullscreen viewer: ←/→ steps through the
  // gallery, Esc closes it.
  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setFullScreen(false);
      else if (e.key === "ArrowLeft") setImgI((i) => (i - 1 + imgs.length) % imgs.length);
      else if (e.key === "ArrowRight") setImgI((i) => (i + 1) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullScreen, imgs.length]);

  // ── Table-view HTML processor ──────────────────────────────────────────
  // Must be declared before any early returns to satisfy Rules of Hooks.
  // Injects concern highlighting into ingredient <li> items by matching
  // their text content against ingredients entries that have a
  // detail_concern. Uses data attributes + click delegation so the popup
  // stays a React-controlled component.
  const processedTableHtml = useMemo(() => {
    const html = detail?.table_view?.html;
    if (!html) return null;

    const rawIngredients = detail?.ingredients || detail?.ocr?.ingredients || [];
    if (!Array.isArray(rawIngredients) || rawIngredients.length === 0) return html;

    // Build a map for ALL ingredients: normalized name → { name, quantity, concern }
    const ingredientMap = new Map();
    for (const ing of rawIngredients) {
      if (ing && typeof ing === "object" && ing.name) {
        ingredientMap.set(ing.name.trim().toUpperCase(), {
          name: ing.name,
          quantity: ing.quantity ?? null,
          concern: ing.detail_concern ?? null,
        });
      }
    }

    // Helper: find entry by exact then partial key match
    const findEntry = (key) => {
      if (ingredientMap.has(key)) return ingredientMap.get(key);
      for (const [mapKey, val] of ingredientMap) {
        if (key.includes(mapKey) || mapKey.includes(key)) return val;
      }
      return null;
    };

    // Replace each <li>TEXT</li> with enriched markup
    return html.replace(/<li>([^<]+)<\/li>/gi, (match, text) => {
      const entry = findEntry(text.trim().toUpperCase());
      if (!entry) return match;

      const hasConcern = !!entry.concern;
      const liColor = hasConcern ? "color:#d94f4f;font-weight:600;" : "";
      const escapedName = hasConcern ? entry.name.replace(/"/g, "&quot;") : "";
      const escapedConcern = hasConcern ? entry.concern.replace(/"/g, "&quot;") : "";
      const concernAttrs = hasConcern
        ? `data-concern-name="${escapedName}" data-concern-text="${escapedConcern}"`
        : "";

      const quantityHtml = entry.quantity
        ? `<span style="font-size:11.5px;color:#6b7280;font-weight:400;margin-left:6px;">(${entry.quantity})</span>`
        : "";

      const badgeHtml = hasConcern
        ? `<span class="concern-eye-btn" title="View concern" style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#fef2f2;border:1.5px solid #fca5a5;cursor:pointer;flex-shrink:0;font-size:11px;font-weight:800;color:#d94f4f;line-height:1;margin-left:5px;">!</span>`
        : "";

      return `<li style="${liColor}" ${concernAttrs}>
        <span style="display:inline-flex;align-items:center;flex-wrap:wrap;gap:2px;">
          <span>${text.trim()}</span>${quantityHtml}${badgeHtml}
        </span>
      </li>`;
    });
  }, [detail]);

  // ── Split the rendered table_view into its two logical sections ────────
  // The backend ships ingredients and nutrition inside one blob of HTML.
  // Rendering that blob as-is crams both into a single card, so pull the
  // two sections apart and let each one live in its own card. Anything we
  // cannot recognise falls back to the untouched markup.
  const tableSections = useMemo(() => {
    if (!processedTableHtml) return null;
    if (typeof window === "undefined" || typeof window.DOMParser !== "function") {
      return { ingredients: null, nutrition: null, raw: processedTableHtml };
    }

    const doc = new window.DOMParser().parseFromString(processedTableHtml, "text/html");
    const pick = (selector) => {
      const node = doc.querySelector(selector);
      if (!node) return null;
      // The card header already names the section — drop the inline heading.
      node.querySelectorAll("h1,h2,h3,h4").forEach((h) => h.remove());
      const html = node.innerHTML.trim();
      return html || null;
    };

    const ingredients = pick(".ingredients-section");
    const nutrition   = pick(".nutrition-section");

    if (!ingredients && !nutrition) {
      return { ingredients: null, nutrition: null, raw: processedTableHtml };
    }
    return { ingredients, nutrition, raw: null };
  }, [processedTableHtml]);

  // ── Guard: product not found / loading states ──────────────────────────
  if (!p) {
    if (loadingDetail || (!detail && !detailError)) {
      return (
        <div className="page-enter" style={{ paddingTop: 20 }}>
          <DetailSkeleton />
        </div>
      );
    }

    if (detailError || !loadingDetail) {
      return (
        <div className="page-enter">
          <div className="empty-st">
            <Package size={40} color="#A4E5E0" />
            <div style={{ fontWeight: 700, color: "#0C6170" }}>Product not found</div>
            {detailError && <div style={{ fontSize: 13, color: "#d94f4f", marginTop: 8 }}>{detailError}</div>}
            <button className="btn btn-g btn-sm" style={{ marginTop: 12 }} onClick={() => navigate("/products")}>
              <ChevronLeft size={14} />Back to Products
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  // ── Actions ────────────────────────────────────────────────────────────
  const doApprove = async (notes = "", ageGroup = "") => {
    try {
      const reviewId = detail?.id;
      if (!reviewId) {
        addToast("Review details not loaded yet. Please wait.", "error");
        return;
      }

      const payload = {
        decision: "approved",
        review_notes: notes || undefined,
        product_name: p.name || detail?.submission?.product_name,
        brand_name: p.brand || detail?.submission?.brand_name,
        category: detail?.submission?.category || p.category || "General",
        age_group: ageGroup || undefined,
      };

      await submitReviewDecision(reviewId, payload);
      setProducts((prev) =>
        prev.map((x) =>
          x.id === productId
            ? { ...x, status: "approved", approvedBy: user.email, approvedDate: new Date().toISOString().split("T")[0], rejectReason: null, ageGroup: ageGroup }
            : x
        )
      );
      addToast("Product approved ✓", "success");
      goToList();
    } catch (err) {
      addToast(err.message || "Failed to approve product", "error");
    }
  };

  const doReject = async (reason) => {
    if (reason.length < 20) {
      addToast("Rejection notes must be at least 20 characters.", "error");
      return;
    }
    try {
      const reviewId = detail?.id;
      if (!reviewId) {
        addToast("Review details not loaded yet. Please wait.", "error");
        return;
      }

      const payload = {
        decision: "rejected",
        review_notes: reason,
        product_name: p.name || detail?.submission?.product_name,
        brand_name: p.brand || detail?.submission?.brand_name,
        category: detail?.submission?.category || p.category || "General",
      };

      await submitReviewDecision(reviewId, payload);
      setProducts((prev) =>
        prev.map((x) =>
          x.id === productId
            ? { ...x, status: "rejected", rejectReason: reason, approvedBy: null, approvedDate: null }
            : x
        )
      );
      addToast("Product rejected ✓", "info");
      goToList();
    } catch (err) {
      addToast(err.message || "Failed to reject product", "error");
    }
  };

  // ── Content renderer ───────────────────────────────────────────────────
  const renderContent = (content) => {
    if (!content) return <span style={{ color: "#A4E5E0" }}>No content extracted</span>;
    if (typeof content === "string" && content.includes("<") && content.includes(">")) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    return <div style={{ whiteSpace: "pre-wrap", color: "#0C6170", lineHeight: 1.6 }}>{content}</div>;
  };

  // Click delegate for ingredient concern eye buttons inside the HTML view
  const handleTableViewClick = (e) => {
    const btn = e.target.closest(".concern-eye-btn");
    if (!btn) return;
    const li = btn.closest("li[data-concern-name]");
    if (!li) return;
    const name = li.getAttribute("data-concern-name");
    const concern = li.getAttribute("data-concern-text");
    if (name && concern) setConcernModal({ open: true, name, concern });
  };

  // Determine the ingredients and nutrition content to show.
  // Always prefer structured OCR data from review-details API.
  // Fall back to legacy string fields only when OCR data is empty.
  const ingredientsData = ocrIngredients.length > 0 ? ocrIngredients : [];
  const nutritionData   = ocrNutrition.length > 0   ? ocrNutrition   : [];
  const ingredientsHtml = ingredientsData.length === 0 ? p.ingredients : null;
  const nutritionHtml   = nutritionData.length === 0   ? p.nutrients   : null;

  // Ingredients carrying a health concern, surfaced in the section header.
  const concernCount = ingredientsData.filter((i) => i?.detail_concern).length;

  // When every nutrient shares one basis show it once in the subtitle;
  // only fall back to a per-row column when the bases actually differ.
  const nutritionBases = [...new Set(nutritionData.map((r) => r.basis).filter(Boolean))];
  const sharedBasis = nutritionBases.length === 1 ? nutritionBases[0] : null;
  const showBasisColumn = nutritionBases.length > 1;

  // ── Submission metadata for the detail side-panel ─────────────────────
  const submissionMeta = detail?.submission ?? null;

  // ── Product Health Score & OCR evaluation data ────────────────────────
  const overallConfidence = detail?.ocr?.overall_confidence != null ? Math.round(detail.ocr.overall_confidence * 100) : null;
  const targetAgeGroup = p.ageGroup ? (AGE_GROUPS_DISPLAY[p.ageGroup] || p.ageGroup) : null;
  const ageGroupScores = detail?.age_group_scores || detail?.submission?.age_group_scores || null;

  // ── product_score_v2: per-stage scores, superseding age_group_scores ──
  const { stageScores, sharedFlags } = parseStageScores(detail);

  // age_group still uses the legacy buckets, which do not map onto the new
  // stages — highlight a target only once it arrives as a stage code.
  const targetStage = p.ageGroup && SCORE_STAGE_RANGES[String(p.ageGroup).toUpperCase()]
    ? String(p.ageGroup).toUpperCase()
    : null;
  const submissionId = detail?.submission_id || submissionMeta?.id || null;
  const productCategory = p.category || submissionMeta?.category || detail?.category || "—";

  const canApprove = can(user.role, "approve") && p.status !== "approved";
  const canReject  = can(user.role, "reject")  && p.status !== "rejected";
  const currentImg = imgs[imgI];

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="page-enter pb-28 md:pb-8">
      {/* ── Sticky page header ─────────────────────────────────────────── */}
      <div className="pw-head">
        {/* Action row */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <button className="btn btn-g btn-sm" onClick={goToList}>
            <ChevronLeft size={14} />
            Back
          </button>

          <div className="flex items-center gap-2">
            {can(user.role, "edit_p") && (
              <button className="btn btn-g btn-sm" onClick={() => navigate(`/products/${p.product_id || productId}/edit`, { state: location.state })}>
                <Pencil size={13} />Edit
              </button>
            )}
            {canApprove && (
              <button className="btn btn-p btn-sm hidden md:inline-flex" onClick={() => setApproveM(true)}>
                <Check size={13} />Approve
              </button>
            )}
            {canReject && (
              <button className="btn btn-d btn-sm hidden md:inline-flex" onClick={() => setRejectM(true)}>
                <X size={13} />Reject
              </button>
            )}
          </div>
        </div>

        {/* Identity row */}
        <div className="pw-eyebrow">{p.isReview ? "Review task" : "Product"}</div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <h1 className="pw-title">{p.name}</h1>
          <StatusBadge status={p.status} />
        </div>

        <div className="pw-chips">
          <Chip icon={Building2} label="Brand" value={p.brand} />
          <Chip icon={Tag} label="Category" value={productCategory} />
          <Chip icon={Hash} label="GTIN" value={submissionMeta?.gtin ?? p.gtin ?? "N/A"} mono />
          {targetAgeGroup && <Chip icon={Baby} label="Age" value={targetAgeGroup} highlight />}
          <Chip icon={User} label="By" value={detail?.submission?.agent?.name || p.createdBy || "Agent"} />
          <Chip icon={Calendar} label="Submitted" value={fmtDate(p.createdDate)} />
        </div>
      </div>

      {/* Detail error banner */}
      {detailError && (
        <div className="pw-note pw-note-bad" style={{ marginTop: 16, display: "flex", gap: 9, alignItems: "flex-start" }}>
          <TriangleAlert size={15} color="#d94f4f" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13.5, color: "#b91c1c", lineHeight: 1.5 }}>{detailError}</div>
        </div>
      )}

      <div style={{ height: 18 }} />

      {loadingDetail ? (
        <DetailSkeleton />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 lg:gap-5 items-start">
          {/* ── Left column ──────────────────────────────────────────── */}
          <div className="flex flex-col gap-4">

            {/* Image gallery */}
            <div className="pw-card">
              <div className="pw-sec-h">
                <div className="pw-sec-icn"><Package size={15} /></div>
                <div className="flex-1 min-w-0">
                  <div className="pw-sec-t">Label Scans</div>
                  <div className="pw-sec-sub">{imgs.length} image{imgs.length !== 1 ? "s" : ""} submitted</div>
                </div>
              </div>

              <div className="pw-sec-b">
                <div className="pw-stage" style={{ height: 240 }}>
                  {currentImg?.url ? (
                    <img
                      src={currentImg.url}
                      alt={currentImg.fileName || "Product scan"}
                      style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "zoom-in" }}
                      onLoad={() => setImgLoading(false)}
                      onError={() => setImgLoading(false)}
                      onClick={() => setFullScreen(true)}
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 56, lineHeight: 1 }}>{currentImg?.emoji}</span>
                      {currentImg?.fileName && (
                        <span style={{ fontSize: 11.5, color: "#37BEB0", fontWeight: 600, textAlign: "center", padding: "0 14px" }}>
                          {currentImg.fileName}
                        </span>
                      )}
                    </div>
                  )}

                  {currentImg?.fileType && (
                    <span className="pw-stage-tag">{currentImg.fileType.replace(/_/g, " ")}</span>
                  )}

                  {currentImg?.url && (
                    <button className="pw-zoom-btn" title="Open fullscreen (Esc to close)" onClick={() => setFullScreen(true)}>
                      <ZoomIn size={15} />
                    </button>
                  )}

                  {imgs.length > 1 && (
                    <>
                      <button className="carousel-btn" style={{ left: 8 }} aria-label="Previous image" onClick={() => setImgI((i) => (i - 1 + imgs.length) % imgs.length)}>
                        <ChevronLeft size={14} />
                      </button>
                      <button className="carousel-btn" style={{ right: 8 }} aria-label="Next image" onClick={() => setImgI((i) => (i + 1) % imgs.length)}>
                        <ChevronRight size={14} />
                      </button>
                      <span className="pw-stage-pill">{imgI + 1} / {imgs.length}</span>
                    </>
                  )}

                  {imgLoading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(248,253,253,.85)", zIndex: 1, pointerEvents: "none" }}>
                      <span className="spin" style={{ display: "inline-block", width: 26, height: 26, border: "2.5px solid rgba(12,97,112,.1)", borderTop: "2.5px solid #37BEB0", borderRadius: "50%" }} />
                    </div>
                  )}
                </div>

                {imgs.length > 1 && (
                  <div className="pw-thumbs">
                    {imgs.map((img, i) => (
                      <button
                        key={img.fileId || i}
                        className={`pw-thumb${i === imgI ? " pw-thumb-a" : ""}`}
                        onClick={() => setImgI(i)}
                        aria-label={img.fileName || `Image ${i + 1}`}
                        aria-current={i === imgI}
                        title={img.fileName || img.fileType?.replace(/_/g, " ") || `Image ${i + 1}`}
                      >
                        {img.url ? <img src={img.url} alt="" /> : <span>{img.emoji}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submission metadata */}
            <div className="pw-card">
              <div className="pw-sec-h">
                <div className="pw-sec-icn"><ClipboardList size={15} /></div>
                <div className="flex-1 min-w-0">
                  <div className="pw-sec-t">{p.isReview ? "Review Task Details" : "Product Details"}</div>
                  {submissionId && <div className="pw-sec-sub" style={{ wordBreak: "break-all" }}>Submission {String(submissionId)}</div>}
                </div>
              </div>

              <div className="pw-sec-b">
                <dl className="pw-dl">
                  {[
                    ["Brand", p.brand, false],
                    ["Category", productCategory, false],
                    ["GTIN", submissionMeta?.gtin ?? p.gtin ?? "N/A", true],
                    targetAgeGroup ? ["Age Group", targetAgeGroup, false] : null,
                    overallConfidence !== null ? ["OCR Accuracy", `${overallConfidence}%`, false] : null,
                    p.isReview ? ["Capture Source", submissionMeta?.capture_source ?? "—", false] : null,
                    p.isReview ? ["Files", submissionMeta ? (submissionMeta.files?.length ?? 0) : "—", false] : null,
                    ["Submitted By", detail?.submission?.agent?.name || p.createdBy || "Agent", false],
                    ["Date", fmtDate(p.createdDate), false],
                  ]
                    .filter(Boolean)
                    .map(([k, v, mono]) => (
                      <div className="pw-dt" key={k}>
                        <dt>{k}</dt>
                        <dd className={mono ? "pw-mono" : undefined}>{v}</dd>
                      </div>
                    ))}
                </dl>

                {p.status === "approved" && p.approvedBy && (
                  <div className="pw-note pw-note-ok" style={{ marginTop: 14 }}>
                    <div className="pw-note-h" style={{ color: "#0C6170" }}><Check size={12} />Approved</div>
                    <div style={{ fontSize: 12.5, color: "#0C6170", fontWeight: 500 }}>By {p.approvedBy}</div>
                    <div style={{ fontSize: 12.5, color: "#37BEB0" }}>{fmtDate(p.approvedDate)}</div>
                    {p.ageGroup && (
                      <div style={{ fontSize: 12.5, color: "#0C6170", marginTop: 5 }}>
                        Age Group: <strong style={{ color: "#062830" }}>{AGE_GROUPS_DISPLAY[p.ageGroup] || p.ageGroup}</strong>
                      </div>
                    )}
                  </div>
                )}

                {p.status === "rejected" && p.rejectReason && (
                  <div className="pw-note pw-note-bad" style={{ marginTop: 14 }}>
                    <div className="pw-note-h" style={{ color: "#d94f4f" }}><X size={12} />Rejected</div>
                    <div style={{ fontSize: 12.5, color: "#b91c1c", lineHeight: 1.6 }}>{p.rejectReason}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Right column ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-4 min-w-0">

            {/* Health scores — product_score_v2 when present, otherwise the
                legacy age_group_scores map. */}
            {stageScores.length > 0 ? (
              <StageScoreCard stageScores={stageScores} sharedFlags={sharedFlags} targetStage={targetStage} />
            ) : ageGroupScores && Object.keys(ageGroupScores).length > 0 ? (
              <div className="pw-card">
                <div className="pw-sec-h">
                  <div className="pw-sec-icn"><Sparkles size={15} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="pw-sec-t">Scores by Age Group</div>
                    <div className="pw-sec-sub">Nutritional &amp; safety rating per age bracket</div>
                  </div>
                </div>

                <div className="pw-sec-b">
                  <div className="pw-ages">
                    {AGE_GROUPS_LIST.map(({ key, label }) => {
                      const score = ageGroupScores[key];
                      const isTarget = targetAgeGroup === label || p.ageGroup === key;
                      const hasScore = score !== undefined && score !== null && score !== "";
                      const numScore = hasScore ? Number(score) : null;
                      const tone = scoreTone(hasScore ? numScore : null);

                      return (
                        <div key={key} className={`pw-age${isTarget ? " pw-age-t" : ""}`}>
                          <div className="flex items-center justify-between gap-1.5">
                            <span className="pw-age-lbl">{label}</span>
                            {isTarget && <span className="pw-tgt">Target</span>}
                          </div>

                          <div className="flex items-baseline gap-1">
                            {hasScore ? (
                              <>
                                <span className="pw-age-val" style={{ color: tone.color }}>{numScore}</span>
                                <span style={{ fontSize: 10.5, color: "#A4E5E0", fontWeight: 700 }}>/ 100</span>
                              </>
                            ) : (
                              <span style={{ fontSize: 14, color: "#c9d5d4", fontWeight: 600 }}>Not scored</span>
                            )}
                          </div>

                          <Meter value={hasScore ? numScore : 0} color={tone.color} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Description / Agent notes */}
            {((p.isReview && submissionMeta?.notes?.trim()) || (!p.isReview && p.description?.trim())) && (
              <div className="pw-card">
                <div className="pw-sec-h">
                  <div className="pw-sec-icn"><MessageSquare size={15} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="pw-sec-t">{p.isReview ? "Agent Notes" : "Description"}</div>
                  </div>
                </div>
                <div className="pw-sec-b">
                  <p style={{ fontSize: 14, color: "#0C6170", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
                    {p.isReview ? submissionMeta.notes : p.description}
                  </p>
                </div>
              </div>
            )}

            {/* Unrecognised table_view markup — render it untouched rather than
                guessing at its structure. */}
            {tableSections?.raw ? (
              <div className="pw-card html-view-card" onClick={handleTableViewClick}>
                <div className="pw-sec-h">
                  <div className="pw-sec-icn"><List size={15} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="pw-sec-t">Extracted Label Data</div>
                    <div className="pw-sec-sub">Read from the submitted scans</div>
                  </div>
                </div>
                <div className="pw-sec-b" style={{ overflowX: "auto" }}>
                  <div dangerouslySetInnerHTML={{ __html: tableSections.raw }} />
                </div>
              </div>
            ) : (
              <>
                {/* ── Ingredients ──────────────────────────────────────── */}
                <div className="pw-card">
                  <div className="pw-sec-h">
                    <div className="pw-sec-icn"><List size={15} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="pw-sec-t">Ingredients</div>
                      <div className="pw-sec-sub">
                        {concernCount > 0
                          ? `${concernCount} flagged with a health concern`
                          : "In the order printed on the label"}
                      </div>
                    </div>
                    {ingredientsData.length > 0 && <span className="pw-count">{ingredientsData.length}</span>}
                  </div>

                  <div className="pw-sec-b">
                    {ingredientsData.length > 0 ? (
                      <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {ingredientsData.map((ing, i) => {
                          const concern = ing.detail_concern;
                          return (
                            <li key={ing.position ?? i} className={`pw-ing${concern ? " pw-ing-flag" : ""}`}>
                              <span className="pw-ing-n">{String(i + 1).padStart(2, "0")}</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ fontWeight: 600, color: concern ? "#b91c1c" : "#062830" }}>{ing.name}</span>
                                {ing.quantity && <span className="pw-ing-q">{ing.quantity}</span>}
                              </span>
                              {concern && (
                                <button
                                  className="pw-flag"
                                  title="View health concern"
                                  onClick={() => setConcernModal({ open: true, name: ing.name, concern })}
                                >
                                  <TriangleAlert size={11} /> Concern
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    ) : tableSections?.ingredients ? (
                      <div className="pw-html-sec ingredients-section" onClick={handleTableViewClick}>
                        <div dangerouslySetInnerHTML={{ __html: tableSections.ingredients }} />
                      </div>
                    ) : ingredientsHtml ? (
                      <div className="ingr-render" style={{ fontSize: 14, lineHeight: 1.8 }}>
                        {renderContent(ingredientsHtml)}
                      </div>
                    ) : (
                      <span style={{ color: "#A4E5E0", fontSize: 13.5 }}>No ingredients data available</span>
                    )}
                  </div>
                </div>

                {/* ── Nutritional Information ──────────────────────────── */}
                <div className="pw-card">
                  <div className="pw-sec-h">
                    <div className="pw-sec-icn"><BarChart2 size={15} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="pw-sec-t">Nutrition Facts</div>
                      <div className="pw-sec-sub">
                        {sharedBasis ? `Per ${sharedBasis}` : "Values as printed on the label"}
                      </div>
                    </div>
                    {nutritionData.length > 0 && <span className="pw-count">{nutritionData.length}</span>}
                  </div>

                  <div className="pw-sec-b pw-sec-b-flush">
                    {nutritionData.length > 0 ? (
                      <div className="tbl-wrap">
                        <table className="tbl">
                          <thead>
                            <tr>
                              <th>Nutrient</th>
                              <th style={{ textAlign: "right" }}>Amount</th>
                              {showBasisColumn && <th style={{ textAlign: "right" }}>Basis</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {nutritionData.map((row) => (
                              <tr key={row.nutrient_key}>
                                <td style={{ color: "#062830", fontWeight: 600 }}>{row.label}</td>
                                <td style={{ textAlign: "right", color: "#0C6170", fontWeight: 800, whiteSpace: "nowrap" }}>
                                  {row.value}
                                  <span style={{ fontWeight: 500, color: "#37BEB0", marginLeft: 3 }}>{row.unit}</span>
                                </td>
                                {showBasisColumn && (
                                  <td style={{ textAlign: "right", color: "#37BEB0", fontWeight: 600, whiteSpace: "nowrap" }}>
                                    {row.basis || "—"}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : tableSections?.nutrition ? (
                      <div className="pw-html-sec nutrition-section" style={{ padding: 18, overflowX: "auto" }}>
                        <div dangerouslySetInnerHTML={{ __html: tableSections.nutrition }} />
                      </div>
                    ) : nutritionHtml ? (
                      <div className="nutr-render" style={{ fontSize: 13.5, padding: 18 }}>
                        {renderContent(nutritionHtml)}
                      </div>
                    ) : (
                      <div style={{ padding: 18, color: "#A4E5E0", fontSize: 13.5 }}>No nutrition facts available</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <RejectModal open={rejectM} onClose={() => setRejectM(false)} onReject={doReject} />

      {/* Ingredient Concern Modal */}
      {concernModal.open && (
        <div
          className="modal-overlay"
          style={{ zIndex: 9999 }}
          onClick={() => setConcernModal({ open: false, name: "", concern: "" })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal-box"
            style={{
              maxWidth: 480,
              padding: 0,
              borderRadius: 16,
              overflow: "hidden",
              background: "#fff",
              boxShadow: "0 20px 48px rgba(6,40,48,0.18)",
              border: "1.5px solid #fca5a5",
            }}
          >
            {/* Modal header */}
            <div style={{ background: "linear-gradient(135deg,#fef2f2 0%,#fff5f5 100%)", padding: "18px 20px 14px", borderBottom: "1px solid #fca5a5", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fef2f2", border: "1.5px solid #fca5a5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <TriangleAlert size={17} color="#d94f4f" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#d94f4f", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 3 }}>Ingredient Concern</div>
                <div style={{ fontFamily: "'Google Sans Flex',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#7f1d1d", lineHeight: 1.35 }}>{concernModal.name}</div>
              </div>
              <button
                className="btn btn-g btn-xs"
                onClick={() => setConcernModal({ open: false, name: "", concern: "" })}
                style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <X size={14} />
              </button>
            </div>
            {/* Modal body */}
            <div style={{ padding: "18px 20px 22px" }}>
              <div style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{concernModal.concern}</div>
            </div>
          </div>
        </div>
      )}

      <ApproveModal
        open={approveM}
        productName={p.name}
        onClose={() => setApproveM(false)}
        onApprove={(notes) => doApprove(notes)}
      />
      <ConfirmModal {...confirmM} onClose={() => setConfirmM({ open: false })} />

      {/* Fullscreen label viewer */}
      {fullScreen && currentImg?.url && (
        <div className="pw-lb" onClick={() => setFullScreen(false)} role="dialog" aria-modal="true">
          <div className="pw-lb-h" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0">
              <div className="pw-lb-t">{currentImg.fileName?.replace(/_/g, " ") || "Image preview"}</div>
              <div style={{ fontSize: 11.5, color: "rgba(219,245,240,.6)", fontWeight: 600 }}>
                {imgI + 1} of {imgs.length} · use ← → to navigate, Esc to close
              </div>
            </div>
            <div className="flex items-center gap-2">
              {imgs.length > 1 && (
                <>
                  <button className="pw-lb-btn" aria-label="Previous image" onClick={() => setImgI((i) => (i - 1 + imgs.length) % imgs.length)}>
                    <ChevronLeft size={17} />
                  </button>
                  <button className="pw-lb-btn" aria-label="Next image" onClick={() => setImgI((i) => (i + 1) % imgs.length)}>
                    <ChevronRight size={17} />
                  </button>
                </>
              )}
              <button className="pw-lb-btn" aria-label="Close" onClick={() => setFullScreen(false)}>
                <X size={17} />
              </button>
            </div>
          </div>

          <div className="pw-lb-body" onClick={(e) => e.stopPropagation()}>
            <img src={currentImg.url} alt={currentImg.fileName || "Product scan"} />
          </div>
        </div>
      )}

      {/* Mobile sticky action bar */}
      {(canApprove || canReject) && (
        <div className="pw-bar md:hidden">
          {canReject && (
            <button className="btn btn-d" onClick={() => setRejectM(true)}>
              <X size={15} /> Reject
            </button>
          )}
          {canApprove && (
            <button className="btn btn-p" onClick={() => setApproveM(true)}>
              <Check size={15} /> Approve
            </button>
          )}
        </div>
      )}
    </div>
  );
}
